/**
 * WooCommerce Utility Functions
 * Shared utility functions for WooCommerce functionality
 */

import popupConfirm from '../common/popupConfirm'

export function showNotice(message, type) {
  let noticesWrapper = document.querySelector('.woocommerce-notices-wrapper')

  // Create notices wrapper if it doesn't exist
  if (!noticesWrapper) {
    noticesWrapper = document.createElement('div')
    noticesWrapper.className = 'woocommerce-notices-wrapper'

    // Try to find a good place to insert it
    const featuresCoupon = document.querySelector('.features-coupon')
    if (featuresCoupon) {
      featuresCoupon.insertBefore(noticesWrapper, featuresCoupon.firstChild)
    } else {
      // Fallback: insert at the top of the body
      document.body.insertBefore(noticesWrapper, document.body.firstChild)
    }
  }

  const notice = document.createElement('div')
  notice.classList.add('woocommerce-message', type === 'error' ? 'woocommerce-error' : 'woocommerce-success')
  notice.innerHTML = message

  noticesWrapper.innerHTML = ''
  noticesWrapper.appendChild(notice)

  window.scrollTo(0, 0)

  setTimeout(() => {
    notice.remove()
  }, 8000)
}

export const debounce = (func, delay) => {
  let timerId
  return async (...args) => {
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = setTimeout(async () => {
      await func(...args)
    }, delay)
  }
}

export async function ajaxReplace(selector) {
  return new Promise(async (resolve, reject) => {
    const element = document.querySelector(selector)
    if (!element) {
      reject('Element not found.')
      return
    }

    try {
      const timestamp = new Date().getTime()
      const response = await fetch(`${window.location.href}?nocache=${timestamp}`)
      const responseText = await response.text()

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = responseText

      const newElement = tempDiv.querySelector(selector)
      if (newElement) {
        element.innerHTML = newElement.innerHTML
      } else {
        reject('New element not found.')
        return
      }

      // Resolve the promise here when the AJAX call completes and the element content is replaced.
      resolve()
    } catch (error) {
      // Error replacing content
      // Reject the promise when there's an error.
      reject(error)
    }
  })
}

// ===================================================================
// DELETE DEVICE FUNCTIONALITY
// ===================================================================

// Initialize delete device functionality
export function initializeDeleteDevice() {
  const deleteForm = document.querySelector('#delete-device')
  const accountContent = document.querySelector('.woocommerce-MyAccount-content')

  if (!deleteForm) return

  deleteForm.addEventListener('submit', async function (e) {
    e.preventDefault()

    const confirmed = confirm('Are you sure you want to remove the device from your list?')
    if (!confirmed) return

    if (accountContent) {
      accountContent.classList.add('show-loading')
    }

    const deleteFormData = new FormData(deleteForm)
    deleteFormData.append('action', 'ownership_delete')
    deleteFormData.append('security', window.garrett?.nonce)

    try {
      const response = await fetch(window.garrett?.ajaxurl, {
        method: 'POST',
        credentials: 'same-origin',
        body: deleteFormData,
      })

      if (response.ok) {
        const responseData = await response.json()

        if (responseData.success) {
          window.location = `${window.location.origin}/my-account/`
        } else {
          // Error in response
          showNotice(responseData.message || 'Error deleting device', 'error')
        }
      } else {
        // Error in response
        showNotice('Error deleting device', 'error')
      }
    } catch (error) {
      // Error in fetch request
      showNotice('Error deleting device', 'error')
    }

    if (accountContent) {
      accountContent.classList.remove('show-loading')
    }
  })
}

// ===================================================================
// FEATURES COUPON FUNCTIONALITY
// ===================================================================

// Initialize features coupon functionality
export function initializeFeaturesCoupon() {
  const featuresCouponWrapper = document.querySelector('.features-coupon__form')

  if (featuresCouponWrapper) {
    fetch(window.garrett?.ajaxurl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        action: 'td_get_user_devices',
        security: window.garrett?.nonce,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          featuresCouponWrapper.innerHTML = data.data.html
          initializeDeviceSelector()
        }
      })
      .catch((error) => {
        // Error occurred
      })
  }
}

// Initialize device selector functionality
export function initializeDeviceSelector() {
  const serialFromUrl = new URLSearchParams(window.location.search).get('serial')
  const openDevicesList = document.querySelector('.open-devices-list')
  const deviceListWrapper = document.querySelector('.device-list-wrapper')
  const devicesList = document.querySelector('.devices-list')
  const form = document.querySelector('.features-coupon__form form')

  if (!openDevicesList || !deviceListWrapper || !devicesList || !form) {
    return
  }

  // Check if user needs to provide billing info (for auto-checkout)
  let userBillingData = null
  let needsBilling = false

  async function checkBillingInfo() {
    try {
      const response = await fetch(window.garrett?.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'get_user_billing_info',
          security: window.garrett?.nonce
        })
      })

      const result = await response.json()

      if (result.success) {
        needsBilling = result.data.needs_billing
        userBillingData = result.data.billing_data

        // Pre-fill form fields with user's existing billing data
        if (userBillingData) {
          const fields = {
            'billing_first_name': userBillingData.first_name,
            'billing_last_name': userBillingData.last_name,
            'billing_email': userBillingData.email,
            'billing_phone': userBillingData.phone,
            'billing_address_1': userBillingData.address_1,
            'billing_city': userBillingData.city,
            'billing_postcode': userBillingData.postcode
          }

          // Fill text inputs
          Object.entries(fields).forEach(([name, value]) => {
            if (value) {
              const field = form.querySelector(`input[name="${name}"]`)
              if (field) {
                field.value = value
              }
            }
          })

          // Fill country select
          if (userBillingData.country) {
            const countryField = form.querySelector('.billing-country-select')
            if (countryField) {
              countryField.value = userBillingData.country
              // Trigger country change to update state field
              updateStateField(userBillingData.country)
            }
          }

          // Fill state field (after country is set and state field is updated)
          if (userBillingData.state) {
            setTimeout(() => {
              const stateSelectEl = form.querySelector('.billing-state-select')
              const stateInputEl = form.querySelector('.billing-state-input')

              if (stateSelectEl && stateSelectEl.style.display !== 'none') {
                stateSelectEl.value = userBillingData.state
              } else if (stateInputEl && stateInputEl.style.display !== 'none') {
                stateInputEl.value = userBillingData.state
              }
            }, 100)
          }
        }
      }
    } catch (error) {
      // Silently fail - we'll show billing fields if checkout fails
    }
  }

  // Check billing info on page load
  checkBillingInfo()

  // Handle country/state dropdown logic for billing fields
  const countrySelect = form.querySelector('.billing-country-select')
  const stateSelect = form.querySelector('.billing-state-select')
  const stateInput = form.querySelector('.billing-state-input')
  const statesDataElement = document.getElementById('billing-states-data')

  let statesData = {}
  if (statesDataElement) {
    try {
      statesData = JSON.parse(statesDataElement.textContent)
    } catch (e) {
      // Fallback to empty object if parsing fails
    }
  }

  function updateStateField(countryCode) {
    if (!stateSelect || !stateInput) return

    const states = statesData[countryCode]

    if (states && Object.keys(states).length > 0) {
      // Country has states - show dropdown
      stateSelect.innerHTML = '<option value="">Select State *</option>'
      Object.entries(states).forEach(([code, name]) => {
        stateSelect.innerHTML += `<option value="${code}">${name}</option>`
      })
      stateSelect.style.display = 'block'
      stateInput.style.display = 'none'
      stateInput.value = '' // Clear text input
    } else {
      // No states - show text input
      stateSelect.style.display = 'none'
      stateInput.style.display = 'block'
      stateSelect.value = '' // Clear dropdown
    }
  }

  // Initialize state field based on pre-selected country (from cookie or default US)
  if (countrySelect && countrySelect.value) {
    updateStateField(countrySelect.value)
  }

  // Handle country change
  if (countrySelect) {
    countrySelect.addEventListener('change', (e) => {
      updateStateField(e.target.value)
    })
  }

  // Handle device list toggle
  openDevicesList.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    deviceListWrapper.style.display = deviceListWrapper.style.display === 'block' ? 'none' : 'block'
    openDevicesList.classList.toggle('active')
  })

  // Handle device selection
  devicesList.addEventListener('click', (e) => {
    e.stopPropagation()
    const listItem = e.target.closest('li')
    if (!listItem) return

    const deviceType = listItem.dataset.type
    const deviceName = listItem.querySelector('strong')?.textContent
    const serialNumber = listItem.querySelector('.sn span')?.textContent

    // Update form inputs
    const serialNumberInput = form.querySelector('input[name="serial_number"]')
    const deviceTypeInput = form.querySelector('input[name="type"]')

    if (serialNumberInput) serialNumberInput.value = serialNumber
    if (deviceTypeInput) deviceTypeInput.value = deviceType

    // Update display
    const displayName = openDevicesList.querySelector('strong')
    const displaySerial = openDevicesList.querySelector('.sn span')

    if (displayName) displayName.textContent = deviceName
    if (displaySerial) displaySerial.textContent = serialNumber

    // Close dropdown
    deviceListWrapper.style.display = 'none'
    openDevicesList.classList.remove('active')
  })

  // Handle form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault()

    const submitButton = form.querySelector('button[name="apply_coupon"]')
    const couponCodeInput = form.querySelector('input[name="coupon_code"]')
    const serialNumberInput = form.querySelector('input[name="serial_number"]')
    const deviceTypeInput = form.querySelector('input[name="type"]')
    const billingFieldsWrapper = form.querySelector('.billing-fields-wrapper')

    if (!couponCodeInput || !serialNumberInput || !deviceTypeInput) {
      showNotice('Form error. Please refresh the page and try again.', 'error')
      return
    }

    // Check if we're in billing collection mode (fields are visible)
    const isInBillingMode = billingFieldsWrapper && billingFieldsWrapper.style.display !== 'none'

    // If billing fields are visible, user is completing checkout with billing info
    if (isInBillingMode) {
      submitButton.classList.add('is-loading')
      submitButton.disabled = true

      try {
        // Collect billing data
        const stateSelectEl = form.querySelector('.billing-state-select')
        const stateInputEl = form.querySelector('.billing-state-input')
        const stateValue = (stateSelectEl && stateSelectEl.style.display !== 'none')
          ? stateSelectEl.value
          : (stateInputEl ? stateInputEl.value : '')

        const billingAddress = {
          first_name: form.querySelector('input[name="billing_first_name"]')?.value || '',
          last_name: form.querySelector('input[name="billing_last_name"]')?.value || '',
          email: form.querySelector('input[name="billing_email"]')?.value || '',
          phone: form.querySelector('input[name="billing_phone"]')?.value || '',
          address_1: form.querySelector('input[name="billing_address_1"]')?.value || '',
          city: form.querySelector('input[name="billing_city"]')?.value || '',
          state: stateValue,
          postcode: form.querySelector('input[name="billing_postcode"]')?.value || '',
          country: form.querySelector('.billing-country-select')?.value || 'US'
        }

        // Validate required fields (state is optional as not all countries have states)
        const requiredFields = ['first_name', 'last_name', 'email', 'address_1', 'city', 'postcode', 'country']
        const missingFields = requiredFields.filter(field => !billingAddress[field])

        if (missingFields.length > 0) {
          showNotice('Please fill in all required billing fields.', 'error')
          submitButton.classList.remove('is-loading')
          submitButton.disabled = false
          return
        }

        // Confirm before placing order
        const confirmed = await popupConfirm(
          'Confirm Order',
          'Your coupon has been validated and is ready to be redeemed.',
          'Would you like to place your order now?',
          'Yes, Place Order',
          'Cancel'
        )

        if (!confirmed) {
          showNotice('Order cancelled. Your coupon is still in the cart.', 'info')
          submitButton.classList.remove('is-loading')
          submitButton.disabled = false
          return
        }

        showNotice('Processing your order...', 'success')

        // Use Store API checkout endpoint with billing data
        const checkoutResponse = await fetch('/wp-json/wc/store/v1/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Nonce': window.garrett?.nonce || ''
          },
          body: JSON.stringify({
            payment_method: '',
            billing_address: billingAddress,
            shipping_address: {}
          })
        })

        const checkoutResult = await checkoutResponse.json()

        if (checkoutResponse.ok && checkoutResult.order_id) {
          // Redirect to thank you page
          window.location.href = `/checkout/order-received/${checkoutResult.order_id}/?key=${checkoutResult.order_key}`
          return
        } else {
          showNotice('Could not complete order. Please try checkout.', 'error')
          submitButton.classList.remove('is-loading')
          submitButton.disabled = false
        }
      } catch (error) {
        showNotice('An error occurred. Please try again.', 'error')
        submitButton.classList.remove('is-loading')
        submitButton.disabled = false
      }
      return
    }

    // Normal coupon redemption flow
    const couponCode = couponCodeInput.value
    const serialNumber = serialNumberInput.value
    const deviceType = deviceTypeInput.value

    if (!couponCode.trim()) {
      showNotice('Please enter a coupon code', 'error')
      return
    }

    if (!serialNumber) {
      showNotice('Please select a device', 'error')
      return
    }

    submitButton.classList.add('is-loading')
    submitButton.disabled = true

    try {
      // Step 1: Validate coupon and get full virtual coupon code
      const validationResponse = await fetch(window.garrett?.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'handle_apply_coupon_code',
          security: window.garrett?.nonce,
          coupon_code: couponCode,
          serial_number: serialNumber,
          device_type: deviceType,
        }),
      })

      const validationResult = await validationResponse.json()

      if (!validationResult.success) {
        showNotice(validationResult.data?.message || 'Invalid coupon code', 'error')
        return
      }

      const fullCouponCode = validationResult.data.coupon_code
      const deviceData = validationResult.data.device_data

      // Step 2: Apply coupon using WooCommerce Store API
      const storeApiResponse = await fetch('/wp-json/wc/store/v1/batch?_locale=site', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Nonce': window.garrett?.nonce || ''
        },
        body: JSON.stringify({
          requests: [
            {
              path: '/wc/store/v1/cart/apply-coupon',
              method: 'POST',
              body: { code: fullCouponCode },
              cache: 'no-store'
            }
          ]
        })
      })

      const storeApiResult = await storeApiResponse.json()

      // Check if coupon was applied successfully
      if (!storeApiResponse.ok || !storeApiResult.responses || storeApiResult.responses[0].status !== 200) {
        const errorMsg = storeApiResult.responses?.[0]?.body?.message || 'Failed to apply coupon'
        showNotice(errorMsg, 'error')
        return
      }

      const cartData = storeApiResult.responses[0].body

      // Verify coupon was actually applied
      if (!cartData.coupons || cartData.coupons.length === 0) {
        showNotice('Coupon was not applied', 'error')
        return
      }

      // Step 3: Attach device metadata to cart items
      const metadataResponse = await fetch(window.garrett?.ajaxurl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'attach_device_metadata_to_cart',
          security: window.garrett?.nonce,
          serial_number: deviceData.serial_number,
          chip_id: deviceData.chip_id,
        }),
      })

      const metadataResult = await metadataResponse.json()

      if (!metadataResult.success) {
        showNotice('Failed to attach device data', 'error')
        return
      }

      // Step 4: Check if we should auto-checkout (virtual + free)
      const allVirtual = cartData.needs_shipping === false
      const totalIsZero = parseInt(cartData.totals.total_price) === 0

      if (allVirtual && totalIsZero) {
        // Check if user needs to provide billing info
        if (needsBilling || !userBillingData) {
          // Show billing fields
          const billingFieldsWrapper = form.querySelector('.billing-fields-wrapper')
          if (billingFieldsWrapper) {
            billingFieldsWrapper.style.display = 'block'

            // Add alert right above the billing fields
            const existingAlert = billingFieldsWrapper.querySelector('.billing-alert')
            if (!existingAlert) {
              const alertDiv = document.createElement('div')
              alertDiv.className = 'billing-alert'
              alertDiv.textContent = 'Please provide your billing information below and submit again.'
              billingFieldsWrapper.insertBefore(alertDiv, billingFieldsWrapper.firstChild)
            }

            // Scroll to billing fields (accounting for sticky header)
            const headerOffset = 160
            const elementPosition = billingFieldsWrapper.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.scrollY - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })

            submitButton.classList.remove('is-loading')
            submitButton.disabled = false
            return
          }
        }

        // User has billing info - confirm before auto-checkout
        const confirmed = await popupConfirm(
          'Confirm Order',
          'Your coupon has been validated and is ready to be redeemed.',
          'Would you like to place your order now?',
          'Yes, Place Order',
          'Cancel'
        )

        if (!confirmed) {
          showNotice('Order cancelled. Your coupon is still in the cart.', 'info')
          submitButton.classList.remove('is-loading')
          submitButton.disabled = false
          return
        }

        showNotice('Processing your order...', 'success')

        // Use Store API checkout endpoint with saved billing data
        const checkoutResponse = await fetch('/wp-json/wc/store/v1/checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Nonce': window.garrett?.nonce || ''
          },
          body: JSON.stringify({
            payment_method: '',
            billing_address: userBillingData || {},
            shipping_address: {}
          })
        })

        const checkoutResult = await checkoutResponse.json()

        if (checkoutResponse.ok && checkoutResult.order_id) {
          // Redirect to thank you page
          window.location.href = `/checkout/order-received/${checkoutResult.order_id}/?key=${checkoutResult.order_key}`
          return
        } else {
          showNotice('Could not complete order. Please try checkout.', 'error')
          submitButton.classList.remove('is-loading')
          submitButton.disabled = false
        }
      }

      showNotice('Coupon applied successfully!', 'success')

      // Import and show drawer with updated cart
      const { showDrawer } = await import('./cart/drawer')
      await showDrawer()

    } catch (error) {
      showNotice('An error occurred. Please try again.', 'error')
    } finally {
      submitButton.classList.remove('is-loading')
      submitButton.disabled = false
    }
  })

  // Handle serial number from URL
  if (serialFromUrl) {
    const matchingDevice = Array.from(devicesList.querySelectorAll('li')).find(li =>
      li.querySelector('.sn span').textContent === serialFromUrl
    )

    if (matchingDevice) {
      matchingDevice.click()
    }
  }
}
